import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { GetGenericAccountsResponse } from "../../../../api/services/client/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiMultiSelect,
} from "../../../../components";
import { ExpandCollapseButton } from "../../../../components/expandCollapseButton/ExpandCollapseButton";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  GENERIC_ACCOUNT_FEATURE_TYPES,
  GENERIC_ACCOUNT_NAVIGATION_FROM,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getFullName,
  getGenericAccountStatusFromExpirationDate,
  isNotEmpty,
} from "../../../../helpers/utils";
import useAccordion from "../../../../hooks/useAccordion";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useMultiSelectARIALabels } from "../../../../hooks/useMultiSelectARIALabels";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import {
  getFeaturesByMatch,
  mapNamesToKey,
} from "../../createGenericAccount/helper";
import { createGenericAccounts } from "../../createGenericAccount/siteCore/SiteCoreKeys";
import { ViewGenericAccountTabContainer } from "./ViewGenericAccountTab.style";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";

const initialAccordionStatus = {
  all: true,
  basicDetails: true,
  configuration: true,
};

type ViewGenericAccountTabType = {
  data?: GetGenericAccountsResponse;
};

type FieldValues = {
  label: string;
  value: string | string[] | React.ReactNode;
  showEllipsis?: boolean;
};

const ViewGenericAccountTab: React.FC<ViewGenericAccountTabType> = ({
  data,
}) => {
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const { isMobile } = useMediaQueries();
  const [basicDetails, setBasicDetails] = useState<FieldValues[]>([]);
  const [configuration, setConfiguration] = useState<FieldValues[]>([]);
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(initialAccordionStatus);
  const location = useLocation();
  const from = location.state?.from || GENERIC_ACCOUNT_NAVIGATION_FROM.VIEW;
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const getFeaturesDropdownList = (key: keyof typeof createGenericAccounts) => {
    return fetchDropdownValues(
      createGenericAccounts.createGenericAccountFeaturesTitle,
      createGenericAccounts[key],
    );
  };

  const getBasicDetailsLabel = (key: keyof typeof createGenericAccounts) => {
    return fetchLabel(
      createGenericAccounts.createGenericAccountBasicDetailsTitle,
      createGenericAccounts[key],
    );
  };

  const getKeyDetailsLabel = (key: keyof typeof createGenericAccounts) => {
    return fetchLabel(
      createGenericAccounts.createGenericAccountKeyDetailsTitle,
      createGenericAccounts[key],
    );
  };

  const getConfigurationLabel = (key: keyof typeof createGenericAccounts) => {
    return fetchLabel(
      createGenericAccounts.createGenericAccountConfigurationTitle,
      createGenericAccounts[key],
    );
  };

  const getCombinedSiteCoreDropDownList = () => {
    const toolsSiteCoreList = getFeaturesDropdownList("toolsList");

    const assessmentsAndTasksSiteCoreList = getFeaturesDropdownList(
      "assessmentsAndTasksList",
    );

    const eventsAndWebinarsSiteCoreList = getFeaturesDropdownList(
      "eventsAndWebinarsList",
    );

    const coachMeetingSiteCoreList =
      getFeaturesDropdownList("coachMeetingList");

    return [
      ...toolsSiteCoreList,
      ...assessmentsAndTasksSiteCoreList,
      ...eventsAndWebinarsSiteCoreList,
      ...coachMeetingSiteCoreList,
    ];
  };

  useEffect(() => {
    if (data && !siteCoreLoading) {
      setBasicDetails([
        {
          label: getBasicDetailsLabel("genericAccountName"),
          value: data?.name,
          showEllipsis: true,
        },
        {
          label: getBasicDetailsLabel("purpose"),
          value: fetchValueByKeyOfDropdown(
            createGenericAccounts.createGenericAccountBasicDetailsTitle,
            createGenericAccounts.purposeList,
            data?.purposeId,
          ),
        },
        {
          label: getBasicDetailsLabel("client"),
          value: data?.client?.name,
          showEllipsis: true,
        },
        {
          label: getBasicDetailsLabel("startDate"),
          value: convertToUserFormat(data?.startDate, userCountryDateFormat),
        },
        {
          label: getBasicDetailsLabel("expirationDate"),
          value: convertToUserFormat(
            data?.expirationDate,
            userCountryDateFormat,
          ),
        },
        {
          label: getKeyDetailsLabel("editViewGenericAccountStatusLabel"),
          value: (
            <StatusBadge
              label={fetchValueByKeyOfDropdown(
                createGenericAccounts.createGenericAccountKeyDetailsTitle,
                createGenericAccounts.editViewGenericAccountStatusList,
                getGenericAccountStatusFromExpirationDate(
                  data?.status,
                  data?.expirationDate,
                ),
              )}
              status={getGenericAccountStatusFromExpirationDate(
                data?.status,
                data?.expirationDate,
              )}
            />
          ),
        },
        {
          label: getBasicDetailsLabel("programDurationDays"),
          value: data?.programDuration ? String(data?.programDuration) : "",
        },
        {
          label: getBasicDetailsLabel("requestedBy"),
          value: data?.requestedByUserId
            ? getFullName(
                data?.requestedByUserId?.firstName,
                data?.requestedByUserId?.lastName,
                true,
              )
            : EMPTY_VALUE_DASH,
          showEllipsis: true,
        },
      ]);

      const updatedFeaturesList = data?.features?.map((item) => ({
        ...item,
        name:
          mapNamesToKey(getCombinedSiteCoreDropDownList())[item.siteCoreKey] ??
          item.name,
      }));

      const toolsList = getFeaturesByMatch(updatedFeaturesList, {
        type: GENERIC_ACCOUNT_FEATURE_TYPES.TOOL,
      }).map((item) => item.name);

      const assessmentList = getFeaturesByMatch(updatedFeaturesList, {
        type: GENERIC_ACCOUNT_FEATURE_TYPES.ASSESSMENT,
      }).map((item) => item.name);

      setConfiguration([
        {
          label: getConfigurationLabel("nextView"),
          value: data.solutionOffering?.name,
          showEllipsis: true,
        },
        {
          label: getConfigurationLabel("configurationCountry"),
          value: data?.country?.name,
        },
        {
          label: getConfigurationLabel("deliveryOffice"),
          value: data?.deliveryOffices?.map((office) => office.name),
        },
        {
          label: getConfigurationLabel("nextProgramClass"),
          value: data.programClass?.name,
        },
        {
          label: getConfigurationLabel("tools"),
          value: isNotEmpty(toolsList) ? toolsList : EMPTY_VALUE_DASH,
        },
        {
          label: getConfigurationLabel("assessmentsAndTasks"),
          value: isNotEmpty(assessmentList) ? assessmentList : EMPTY_VALUE_DASH,
        },
      ]);
    }
  }, [data, siteCoreLoading]);

  return (
    <ViewGenericAccountTabContainer>
      <ExpandCollapseButton
        accordionStatus={accordionStatus}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
      />

      <MuiBox className="basic-details">
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_BASIC_DETAILS}
        >
          <AccordionHeader
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(
                  createGenericAccounts.createGenericAccountBasicDetailsTitle,
                )}
              />
            }
            onChange={() => handleIndividualAccordionChange("basicDetails")}
            isExpanded={accordionStatus.basicDetails}
          >
            <MuiGrid
              container
              columnSpacing={isMobile ? 0 : 2}
              rowSpacing={isMobile ? 2 : 5}
              className="details-container"
            >
              {basicDetails.map((item) => (
                <MuiGrid size={gridSize} key={item.label}>
                  <MuiDoubleLabel
                    label={item.label}
                    replaceEmptyWithDash={true}
                    isValueWithEllipsis={item.showEllipsis}
                    value={item.value}
                  />
                </MuiGrid>
              ))}
            </MuiGrid>
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>

      <MuiBox className="configuration">
        <PermissionWrapper
          permission={MODULE_PERMISSIONS.GENERIC_ACCOUNT_VIEW_CONFIGURATION}
        >
          <AccordionHeader
            title={
              <AccordionHeaderText
                label={fetchComponentTitle(
                  createGenericAccounts.createGenericAccountConfigurationTitle,
                )}
              />
            }
            onChange={() => handleIndividualAccordionChange("configuration")}
            isExpanded={accordionStatus.configuration}
          >
            <MuiGrid
              container
              columnSpacing={isMobile ? 0 : 2}
              rowSpacing={isMobile ? 2 : 5}
              className="configuration-container"
            >
              {configuration.map((item) =>
                !Array.isArray(item.value) ? (
                  <MuiGrid size={gridSize} key={item.label}>
                    <MuiDoubleLabel
                      label={item.label}
                      replaceEmptyWithDash={true}
                      isValueWithEllipsis={item.showEllipsis}
                      value={item.value}
                    />
                  </MuiGrid>
                ) : (
                  <MuiGrid size={gridSize} key={item.label}>
                    <MuiMultiSelect
                      label={item.label}
                      showChip={true}
                      options={item.value.map((val) => ({
                        label: val,
                        value: val,
                      }))}
                      hidden
                      selectedValues={item.value}
                      isChipEditable={false}
                      showDropdown={false}
                      dialogWidth={376}
                      dialogTitle={item.label}
                      selectPlaceHolder={commonLabels.selectPlaceHolder}
                      searchPlaceHolder={commonLabels.search}
                      selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                      doneText={commonLabels.done}
                      allARIAProps={multiSelectARIALabels}
                    />
                  </MuiGrid>
                ),
              )}
            </MuiGrid>
          </AccordionHeader>
        </PermissionWrapper>
      </MuiBox>
    </ViewGenericAccountTabContainer>
  );
};

export default ViewGenericAccountTab;
